import { Component, effect, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { AuthService, GenericError } from '@auth0/auth0-angular';
import { IntercomService, IntercomTokenService } from '@bannerflow/intercom-messenger';
import { registerJmesCustomFunctions, CustomFunctionContext } from '@bannerflow/jmespath-customfunctions';
import { NS_CONFIG_TOKEN } from '@bannerflow/notification';
import { UINewThemeService } from '@bannerflow/ui';
import { firstValueFrom } from 'rxjs';
import { filter, mergeMap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { initGTM } from 'src/plugins/scripts/gtm';
import { AccountAccessService } from './services/api/account-access.service';

@Component({
    selector: 'app-root',
    imports: [RouterOutlet],
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss']
})
export class AppComponent {
    private authService = inject(AuthService);
    private nsConfig = inject(NS_CONFIG_TOKEN);
    private user = toSignal(this.authService.user$);
    private uiThemeService = inject(UINewThemeService);
    private intercomService = inject(IntercomService);
    private intercomTokenService = inject(IntercomTokenService);
    private accountAccessService = inject(AccountAccessService);
    private router = inject(Router);
    private intercomInitialized = false;

    constructor() {
        this.uiThemeService.enableNewTheme();
        this.initIntercom().catch(err => console.error('Intercom init failed', err));
        this.setupGTM();

        effect(() => {
            const user = this.user();
            if (user) {
                this.authService.getAccessTokenSilently({ cacheMode: 'on' }).subscribe(accessToken => {
                    this.nsConfig.accessToken$.next(accessToken);
                });
            }
        });
        this.authService.error$
            .pipe(
                filter(
                    e =>
                        e instanceof GenericError &&
                        (e.error === 'login_required' ||
                            e.error === 'invalid_grant' ||
                            e.error === 'missing_refresh_token')
                ),
                mergeMap(() => this.authService.loginWithRedirect())
            )
            .subscribe();
    }

    ngOnInit() {
        this.accountAccessService.user$.subscribe(user => {
            const context: CustomFunctionContext = {
                brandId: user.brand.id,
                environment: environment.stage
            };
            registerJmesCustomFunctions(context);
        });
    }

    private async initIntercom(): Promise<void> {
        if (this.intercomInitialized) return;
        const isEnabledStage = environment.stage === 'sandbox' || environment.stage === 'production';
        if (!isEnabledStage) return;
        await this.intercomService.init({
            appId: environment.intercom.appId,
            jwtProvider: async () => {
                const isAuthenticated = await firstValueFrom(this.authService.isAuthenticated$);
                return isAuthenticated ? ((await this.intercomTokenService.getToken()) ?? '') : '';
            }
        });
        this.intercomInitialized = true;
        this.intercomService.boot();
        this.setupIntercomPaddingOnRouteChange();
    }

    private setupIntercomPaddingOnRouteChange(): void {
        const getPadding = (url: string) => ({ vertical_padding: url.includes('feed-editor') ? 20 : 110 });

        this.intercomService.update(getPadding(this.router.url));

        this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(event => {
            this.intercomService.update(getPadding((event as NavigationEnd).urlAfterRedirects));
        });
    }

    private setupGTM(): void {
        if (environment.gtm.id && environment.stage !== 'local' && environment.stage !== 'proxy') {
            initGTM(environment.gtm.id);
        }
    }
}
